import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FiChevronsDown } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { useNavbarContext } from '../../../contexts/NavbarProvider';
import useFeatureBasedPermissions from '../../../hooks/useFeatureBasedPermissions';
import usePersistentState from '../../../hooks/usePersistentState';
import { NavButton } from '../../../types/common';
import { rootPath } from '../../../utils/commonFunctions';
import SubNavLinkButton from './SubNavLinkButton';

const CollapsibleSideNavButton = ({
  route,
  onClick,
}: {
  route: NavButton;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}) => {
  // integration of react hooks here
  const [subReadPermissionCount, setSubReadPermissionCount] =
    useState<number>(0);

  // integration of custom hooks here
  const [showMenu, setShowMenu] = usePersistentState<boolean>(
    false,
    route.matcher
  );

  // integration of react-router-dom hooks here
  const { pathname } = useLocation();

  // integration of context hooks here
  const { showNavbar, isMobile } = useNavbarContext();
  const { currentUserType } = useAuthContext();

  // integration of custom hooks here
  const { hasReadPermission } = useFeatureBasedPermissions('');

  // checking if any of the sub route has read permission or not here
  useEffect(() => {
    if (route.subRoutes) {
      const readPermissionCount = route.subRoutes.reduce(
        (count, route) =>
          route.whichFeature && hasReadPermission(route.whichFeature)
            ? (count += 1)
            : count,
        0
      );

      setSubReadPermissionCount(readPermissionCount);
    }
  }, [hasReadPermission, route.subRoutes]);

  // rendering collapsible side nav button component here
  return (
    <>
      {route.accessibleTo === 'all' ||
      (currentUserType === 'client' && route.accessibleTo === 'client') ? (
        <div title={!showNavbar ? route.title : ''} className='relative'>
          <div onClick={onClick} className='relative'>
            <button
              onClick={() => setShowMenu((state: boolean) => !state)}
              className={`relative w-full block pr-2 py-4 pl-7 text-xl text-legal-brown duration-300 after:h-full after:w-0 after:absolute after:left-0 after:top-0 after:origin-left after:duration-300 whitespace-nowrap z-[5] italic ${
                rootPath(pathname, true) === route.matcher && !showMenu
                  ? '!text-legal-dark-blue hover:opacity-70'
                  : 'hover:after:w-full after:bg-legal-dark-gray hover:text-base-white'
              }`}
            >
              <span className='relative flex items-center gap-3 z-10'>
                <span>{route.icon}</span>
                <motion.p
                  initial={
                    showNavbar ? { x: 0 } : isMobile ? { x: 0 } : { x: -260 }
                  }
                  animate={
                    showNavbar ? { x: 0 } : isMobile ? { x: 0 } : { x: -260 }
                  }
                >
                  {route.title}
                </motion.p>
              </span>

              {showNavbar ? (
                <div
                  className={`absolute right-3 top-1/2 -translate-y-1/2 z-[5] duration-300 ${
                    showMenu ? 'rotate-180' : 'rotate-0'
                  }`}
                >
                  <FiChevronsDown className='h-6 w-6' />
                </div>
              ) : null}
            </button>

            <motion.div
              initial={{ width: 0 }}
              animate={
                rootPath(pathname, true) === route.matcher && !showMenu
                  ? { width: '100%' }
                  : { width: 0 }
              }
              className='absolute h-full w-full bg-legal-brown top-0 left-0'
            ></motion.div>
          </div>

          <AnimatePresence
            initial={false}
            mode='sync'
            onExitComplete={() => null}
          >
            {showMenu ? (
              <motion.div
                initial={{ height: 0, opacity: 0, y: -50 }}
                animate={{ height: 'fit-content', opacity: 1, y: 0 }}
                exit={{ height: 0, opacity: 0, y: -50 }}
                className='relative'
              >
                {currentUserType === 'client'
                  ? route.subRoutes
                      ?.filter((subRoute) => subRoute.accessibleTo === 'client')
                      .map((subRoute, index) => (
                        <SubNavLinkButton
                          key={index}
                          subRoute={subRoute}
                          pathname={pathname}
                          routeTitle={route.title}
                        />
                      ))
                  : route.subRoutes?.map((subRoute, index) => (
                      <SubNavLinkButton
                        key={index}
                        subRoute={subRoute}
                        pathname={pathname}
                        routeTitle={route.title}
                      />
                    ))}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      ) : subReadPermissionCount > 0 ? (
        <div title={!showNavbar ? route.title : ''} className='relative'>
          <div onClick={onClick} className='relative'>
            <button
              onClick={() => setShowMenu((state: boolean) => !state)}
              className={`relative w-full block pr-2 py-4 pl-7 text-xl text-legal-brown duration-300 after:h-full after:w-0 after:absolute after:left-0 after:top-0 after:origin-left after:duration-300 whitespace-nowrap z-[5] italic ${
                rootPath(pathname, true) === route.matcher && !showMenu
                  ? '!text-legal-dark-blue hover:opacity-70'
                  : 'hover:after:w-full after:bg-legal-dark-gray hover:text-base-white'
              }`}
            >
              <span className='relative flex items-center gap-3 z-10'>
                <span>{route.icon}</span>
                <motion.p
                  initial={
                    showNavbar ? { x: 0 } : isMobile ? { x: 0 } : { x: -260 }
                  }
                  animate={
                    showNavbar ? { x: 0 } : isMobile ? { x: 0 } : { x: -260 }
                  }
                >
                  {route.title}
                </motion.p>
              </span>

              {showNavbar ? (
                <div
                  className={`absolute right-3 top-1/2 -translate-y-1/2 z-[5] duration-300 ${
                    showMenu ? 'rotate-180' : 'rotate-0'
                  }`}
                >
                  <FiChevronsDown className='h-6 w-6' />
                </div>
              ) : null}
            </button>

            <motion.div
              initial={{ width: 0 }}
              animate={
                rootPath(pathname, true) === route.matcher && !showMenu
                  ? { width: '100%' }
                  : { width: 0 }
              }
              className='absolute h-full w-full bg-legal-brown top-0 left-0'
            ></motion.div>
          </div>

          <AnimatePresence
            initial={false}
            mode='sync'
            onExitComplete={() => null}
          >
            {showMenu ? (
              <motion.div
                initial={{ height: 0, opacity: 0, y: -50 }}
                animate={{ height: 'fit-content', opacity: 1, y: 0 }}
                exit={{ height: 0, opacity: 0, y: -50 }}
                className='relative'
              >
                {route.subRoutes?.map((subRoute, index) =>
                  subRoute.whichFeature ? (
                    hasReadPermission(subRoute.whichFeature) ? (
                      <SubNavLinkButton
                        key={index}
                        subRoute={subRoute}
                        pathname={pathname}
                        routeTitle={route.title}
                      />
                    ) : null
                  ) : null
                )}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      ) : null}
    </>
  );
};

export default CollapsibleSideNavButton;
